import { FC, Fragment, useState } from 'react';
import {
    Alert,
    Button,
    Dialog as BaseDialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    styled,
    Tooltip,
    TableContainer,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { CancelOutlined, SaveOutlined, SearchOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { Campaign } from '../../../../../types/Campaign';
import { CDTextField } from '../../../../../components/CDTextField';
import ApiService from '../../../../../ApiService';
import Column from '../../../../../components/Column';
import Row from '../../../../../components/Row';
import Utils from '../../../../../components/Utils';

const Dialog = styled(BaseDialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(4, 2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(2),
    },
}));

interface AssignDspCampaignDialogProps {
    campaign: Campaign;
    onClose(dspCampaignId: string | null): void;
}

const AssignDspCampaignDialog: FC<AssignDspCampaignDialogProps> = ({ campaign, onClose }) => {
    const [dspCampaignId, setDspCampaignId] = useState('');
    const [dspCampaign, setDspCampaign] = useState<Campaign | null>(null);
    const [duplicate, setDuplicate] = useState(false);
    const [dspCampaignExists, setDspCampaignExists] = useState(true);

    function validateDspCampaign() {
        if (campaign.dealerId && dspCampaignId) {
            ApiService.validateDspCampaign(campaign.dealerId, dspCampaignId).then((response) => {
                setDuplicate(response.data.duplicate);
                setDspCampaign(response.data.campaign);
                setDspCampaignExists(response.data.campaign !== null);
            });
        }
    }

    return (
        <Fragment>
            <Dialog maxWidth="sm" open={true} fullWidth onClose={() => onClose(null)}>
                <DialogTitle>Assign DSP Campaign</DialogTitle>
                <DialogContent dividers>
                    <Column>
                        <Row>
                            <CDTextField
                                label="DSP Campaign ID"
                                value={dspCampaignId}
                                onChange={(event) => setDspCampaignId(event.target.value)}
                                shrinkLabel
                                sx={{ marginTop: '10px' }}
                            />
                            <Tooltip title="DSP Campaign Search">
                                <IconButton color="primary" sx={{ marginTop: '8px' }} onClick={validateDspCampaign}>
                                    <SearchOutlined />
                                </IconButton>
                            </Tooltip>
                        </Row>

                        {!dspCampaignExists && <Alert severity="error">Invalid DSP Campaign ID</Alert>}

                        {duplicate && (
                            <Alert severity="error">
                                This Campaign ID is already assigned to another Campaign: <br />
                                <Link to={'/campaign/' + dspCampaign?.id} onClick={() => onClose(null)}>
                                    {dspCampaign?.dealer?.dealerName} / {dspCampaign?.campaignName}
                                </Link>
                            </Alert>
                        )}

                        {dspCampaign && (
                            <TableContainer variant="outlined" component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Campaign Name</TableCell>
                                            <TableCell align="right">Status</TableCell>
                                            <TableCell align="right">Budget</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell align="left">{dspCampaign.campaignName}</TableCell>
                                            <TableCell align="right">
                                                {dspCampaign.deliveryStatus.internalStatus}
                                            </TableCell>
                                            <TableCell align="right">
                                                {Utils.formatCurrency(dspCampaign.budget)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Column>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelOutlined />}
                        onClick={() => {
                            onClose(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={dspCampaign === null || duplicate}
                        onClick={() => {
                            onClose(dspCampaignId);
                        }}
                        startIcon={<SaveOutlined />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export type { AssignDspCampaignDialogProps };
export default AssignDspCampaignDialog;
